import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { Loader2 } from "lucide-react";
import type { CommonReportFilters, ReportCategory } from "../types";

interface ReportFiltersProps {
  category: ReportCategory;
  filters: CommonReportFilters;
  onChange: (next: CommonReportFilters) => void;
  onRun: () => void;
  onReset: () => void;
  loading: boolean;
}

const LABEL_CLASS =
  "block text-[11px] font-medium uppercase tracking-wide text-muted-foreground";

export function ReportFilters({
  category,
  filters,
  onChange,
  onRun,
  onReset,
  loading,
}: ReportFiltersProps) {
  const showCompany =
    category === "projects" || category === "finance" || category === "work";
  const showProject = category !== "people" || true; // always allow project for cross-cutting
  const showAssignee = category === "people" || category === "work";
  const showStatus = category === "projects";
  const showCurrency = category === "finance";

  const companyList = useCompanyList({ page: 1, limit: 200 });
  const projectList = useProjectList({ page: 1, limit: 200 });
  const userList = useUserList({ page: 1, limit: 100 });

  function update<K extends keyof CommonReportFilters>(
    key: K,
    value: CommonReportFilters[K],
  ) {
    onChange({ ...filters, [key]: value });
  }

  return (
    <div
      className="rounded-xl border bg-white p-4"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-[150px]">
          <label className={LABEL_CLASS}>Date From</label>
          <Input
            type="date"
            value={filters.dateFrom ?? ""}
            onChange={(e) => update("dateFrom", e.target.value || undefined)}
          />
        </div>
        <div className="w-[150px]">
          <label className={LABEL_CLASS}>Date To</label>
          <Input
            type="date"
            value={filters.dateTo ?? ""}
            onChange={(e) => update("dateTo", e.target.value || undefined)}
          />
        </div>

        {showCompany && (
          <div className="w-[180px]">
            <label className={LABEL_CLASS}>Company</label>
            <Select
              value={filters.companyId ?? ""}
              onChange={(e) =>
                update("companyId", e.target.value || undefined)
              }
            >
              <option value="">All companies</option>
              {(companyList.data?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        )}

        {showProject && (
          <div className="w-[200px]">
            <label className={LABEL_CLASS}>Project</label>
            <Select
              value={filters.projectId ?? ""}
              onChange={(e) =>
                update("projectId", e.target.value || undefined)
              }
            >
              <option value="">All projects</option>
              {(projectList.data?.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </div>
        )}

        {showAssignee && (
          <div className="w-[180px]">
            <label className={LABEL_CLASS}>
              {category === "people" ? "Team Member" : "Assignee"}
            </label>
            <Select
              value={
                category === "people"
                  ? filters.userId ?? ""
                  : filters.assignedToId ?? ""
              }
              onChange={(e) => {
                const v = e.target.value || undefined;
                if (category === "people") update("userId", v);
                else update("assignedToId", v);
              }}
            >
              <option value="">Everyone</option>
              {(userList.data?.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </div>
        )}

        {showStatus && (
          <div className="w-[150px]">
            <label className={LABEL_CLASS}>Status</label>
            <Select
              value={filters.status ?? ""}
              onChange={(e) => update("status", e.target.value || undefined)}
            >
              <option value="">All</option>
              <option value="PLANNING">Planning</option>
              <option value="ACTIVE">Active</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </div>
        )}

        {showCurrency && (
          <div className="w-[120px]">
            <label className={LABEL_CLASS}>Currency</label>
            <Select
              value={filters.currency ?? ""}
              onChange={(e) =>
                update("currency", e.target.value || undefined)
              }
            >
              <option value="">All</option>
              <option value="AED">AED</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </Select>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            className="text-[12px] text-[hsl(210,100%,40%)] hover:underline"
          >
            Reset Filters
          </button>
          <Button onClick={onRun} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : null}
            Run Report
          </Button>
        </div>
      </div>
    </div>
  );
}
